// SSE client manager using native EventSource
// - Supports multiple listeners per event type
// - Configurable delays for connection establishment and cleanup
// - Auto-cleanup with configurable delay when no subscribers remain
// - Optional ping-based activity watchdog and polling-mode fallback

import * as Sentry from "@sentry/react";
import { isEqual } from "lodash";

import { diagnosticEventLog } from "./diagnostic/DiagnosticEventLog";
import { redactUrl } from "./diagnostic/redactUrl";
import type { DiagnosticSource } from "./diagnostic/types";

type Listener<T> = (data: T) => void;

type Status = "active" | "inactive" | "pending";

interface PollingConfig {
  pollingFn: () => Promise<void>;
  intervalMs: number;
}

interface SSEConfig {
  connectionName: string;
  endpoint: string;
  retryEnabled?: boolean;
  cleanupDelayMs?: number; // Delay before closing connection when no subscribers remain
  connectionDelayMs?: number; // Delay before establishing the SSE connection
  // Activity watchdog: if no ping/message for this duration, treat as a stall.
  // Requires the backend to emit `event: ping` keepalives at a faster cadence.
  activityTimeoutMs?: number;
  // Consecutive failures (errors or activity timeouts) before entering polling mode.
  fallbackThreshold?: number;
  // Polling fallback config. When provided and threshold reached, the manager
  // calls pollingFn on the configured interval until SSE recovers.
  pollingConfig?: PollingConfig;
}

interface ConnectionParams {
  urlParams?: Record<string, string>;
}

const STABILITY_WINDOW_MS = 30000;

export class SSEClientManager {
  private sse: EventSource | null = null;
  private status: Status = "inactive";
  private listeners: Record<string, Listener<unknown>[]> = {};
  private config: SSEConfig;
  private currentConnection: ConnectionParams | null = null;

  private retryCount = 0;
  private retryTimerRef: number | undefined;
  private cleanupTimerRef: number | undefined;
  private connectionTimerRef: number | undefined;

  // Watchdog + fallback state
  private lastActivityAt = 0;
  private watchdogTimerRef: number | undefined;
  private failureCount = 0;
  private stabilityTimerRef: number | undefined;
  private pollingMode = false;
  private pollingTimerRef: number | undefined;
  private pollingModeListeners: Set<(isPolling: boolean) => void> = new Set();
  private boundPingHandler: ((event: Event) => void) | null = null;

  // Visibility-based recovery state. EventSource on a long-hidden/throttled
  // tab can silently die without firing onerror, and the watchdog timer is
  // frozen while hidden — so nothing reconnects on return without this.
  private isHidden = false;
  private hiddenSince = 0;
  private boundVisibilityHandler: (() => void) | null = null;
  private recoveryInFlight = false;

  constructor(config: SSEConfig) {
    this.config = config;
    // Registered once for the lifetime of this (module-singleton) manager;
    // intentionally never removed in cleanup() so it survives reconnects.
    this.startVisibilityTracking();
  }

  /** Source string for diagnostic event log entries (mirrors ConnectionManager) */
  private get diagnosticSource(): DiagnosticSource {
    return `${this.config.connectionName.toLowerCase()}:sse`;
  }

  start(params: ConnectionParams) {
    // if the urlParams have changed, restart the connection
    if (!isEqual(this.currentConnection?.urlParams, params.urlParams)) {
      this.cleanup();
    }
    // If already connecting or connected, do nothing
    else if (this.status == "pending" || this.status == "active") {
      return;
    }

    this.currentConnection = params;
    this.cleanup();
    this.setStatus("pending");

    const connectionDelayMs = this.config.connectionDelayMs ?? 0;

    if (connectionDelayMs > 0) {
      console.log(
        `Delaying ${this.config.connectionName} SSE connection by ${connectionDelayMs}ms...`
      );
      this.connectionTimerRef = setTimeout(() => {
        this.establishConnection(params);
      }, connectionDelayMs);
    } else {
      this.establishConnection(params);
    }
  }

  private establishConnection(params: ConnectionParams) {
    let endpoint;

    try {
      endpoint = new URL(this.config.endpoint);
    } catch {
      // If the endpoint is a relative URL, construct it using the current origin
      endpoint = new URL(this.config.endpoint, window.location.origin);
    }

    if (params.urlParams) {
      Object.entries(params.urlParams).forEach(([key, value]) => {
        endpoint.searchParams.append(key, value);
      });
    }

    diagnosticEventLog.record(this.diagnosticSource, "info", "connection-attempt", {
      url: redactUrl(endpoint.toString()),
    });
    this.sse = new EventSource(endpoint.toString());
    this.recordActivity();

    // Named ping events do not fire `onmessage` — register explicitly.
    if (this.config.activityTimeoutMs) {
      this.boundPingHandler = () => this.recordActivity();
      this.sse.addEventListener("ping", this.boundPingHandler);
      this.startWatchdog();
    }

    this.sse.onmessage = (event: MessageEvent) => {
      this.recordActivity();

      if (!event.data) return;

      const parsed = this.safeParse(event.data);

      (this.listeners[event.type] || []).forEach((fn) => {
        if (!parsed) {
          console.error("Failed to parse SSE message", event.data);
          return;
        }
        try {
          fn(parsed);
        } catch (error) {
          console.error("Error in message callback:", error);
          console.error("Message data:", parsed);
          Sentry.captureException(error, {
            extra: {
              messageData: parsed,
              connectionName: this.config.connectionName,
            },
          });
        }
      });
    };

    this.sse.onopen = () => {
      console.log(`Connected to ${this.config.connectionName} SSE`);

      this.setStatus("active");
      this.retryCount = 0;
      this.recoveryInFlight = false;
      this.recordActivity();
      this.scheduleStabilityReset();
      // If we recover, exit polling mode immediately.
      this.exitPollingMode();
    };

    // on error, close to suppress native auto-reconnect, then retry with custom backoff
    this.sse.onerror = () => {
      console.error(`${this.config.connectionName} SSE connection error`);

      diagnosticEventLog.record(this.diagnosticSource, "error", "sse-error", {
        retryCount: this.retryCount,
      });
      this.recoveryInFlight = false;
      this.setStatus("inactive");
      this.handleFailure();
      this.cleanup({ preservePollingState: true });
      if (this.config.retryEnabled !== false) {
        this.reconnectWithDelay();
      }
    };
  }

  /**
   * Update stored URL params without reconnecting.
   * Ensures the next reconnect (e.g. after an error) uses the latest params.
   */
  updateUrlParams(urlParams: Record<string, string>) {
    if (this.currentConnection) {
      this.currentConnection = {
        ...this.currentConnection,
        urlParams,
      };
    }
  }

  subscribe<T>(type: "message" | "onStatusChange", callback: Listener<T>) {
    // Clear any pending cleanup since we have a new subscriber
    clearTimeout(this.cleanupTimerRef);
    this.cleanupTimerRef = undefined;

    this.listeners[type] = this.listeners[type] || [];
    this.listeners[type].push(callback as Listener<unknown>);

    if (type === "onStatusChange") {
      setTimeout(() => callback(this.status as T), 0);
    }

    return () => this.unsubscribe(type, callback);
  }

  unsubscribe<T>(type: "message" | "onStatusChange", callback: Listener<T>) {
    this.listeners[type] = (this.listeners[type] || []).filter(
      (fn) => fn !== callback
    );

    // Prune empty listener arrays to prevent unbounded growth
    if (this.listeners[type]?.length === 0) {
      delete this.listeners[type];
    }

    // Auto-close if no subscribers remain after configurable delay
    // Don't close immediately to allow component remounts on dashboard tab switching
    // When switching tabs, components unmount then remount quickly, in the process calling unsubscribe then subscribe
    const cleanupDelayMs = this.config.cleanupDelayMs ?? 3000;

    // Clear any existing cleanup timer
    clearTimeout(this.cleanupTimerRef);

    this.cleanupTimerRef = setTimeout(() => {
      if (this.hasNoSubscribers()) {
        this.cleanup();
        this.exitPollingMode();
      }
    }, cleanupDelayMs);
  }

  /** Subscribe to polling-mode transitions. Fires immediately with current state. */
  onPollingModeChange(callback: (isPolling: boolean) => void) {
    this.pollingModeListeners.add(callback);
    setTimeout(() => callback(this.pollingMode), 0);
    return {
      unsubscribe: () => {
        this.pollingModeListeners.delete(callback);
      },
    };
  }

  getIsPollingMode(): boolean {
    return this.pollingMode;
  }

  private reconnectWithDelay() {
    if (!this.currentConnection) {
      console.error("Cannot reconnect: no current connection stored");
      return;
    }

    const delayInMs = (this.retryCount + 1) * 3000;

    console.log(
      `Reconnecting to ${this.config.connectionName} SSE in ${delayInMs / 1000} seconds...`
    );

    this.retryTimerRef = setTimeout(
      () => this.start(this.currentConnection!),
      delayInMs
    );

    this.retryCount++;
  }

  private setStatus(newStatus: Status) {
    if (this.status !== newStatus) {
      const oldStatus = this.status;
      this.status = newStatus;
      diagnosticEventLog.record(this.diagnosticSource, "info", "status-change", {
        from: oldStatus,
        to: newStatus,
      });

      (this.listeners["onStatusChange"] ?? []).forEach((fn) => {
        try {
          fn(newStatus);
        } catch {
          Sentry.captureException(new Error("Error in status change callback"));
        }
      });
    }
  }

  private hasNoSubscribers() {
    return Object.keys(this.listeners).every(
      (key) => this.listeners[key].length === 0
    );
  }

  private cleanup(opts: { preservePollingState?: boolean } = {}) {
    clearTimeout(this.retryTimerRef);
    clearTimeout(this.cleanupTimerRef);
    clearTimeout(this.connectionTimerRef);
    clearTimeout(this.stabilityTimerRef);
    clearInterval(this.watchdogTimerRef);

    if (this.sse) {
      if (this.boundPingHandler) {
        this.sse.removeEventListener("ping", this.boundPingHandler);
        this.boundPingHandler = null;
      }
      this.sse.close();
      this.sse = null;
    }

    this.retryTimerRef = undefined;
    this.cleanupTimerRef = undefined;
    this.connectionTimerRef = undefined;
    this.stabilityTimerRef = undefined;
    this.watchdogTimerRef = undefined;

    if (!opts.preservePollingState) {
      this.failureCount = 0;
    }
  }

  private safeParse<T = unknown>(data: string): T | null {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  // ---------------- Watchdog + fallback internals ----------------

  private recordActivity() {
    this.lastActivityAt = Date.now();
  }

  private startWatchdog() {
    const timeoutMs = this.config.activityTimeoutMs;
    if (!timeoutMs) return;
    clearInterval(this.watchdogTimerRef);
    this.lastActivityAt = Date.now();
    this.watchdogTimerRef = setInterval(() => {
      if (this.status !== "active") return;
      // Browsers freeze timers + throttle message delivery while hidden, so a
      // post-hide tick would otherwise read a huge stale gap and fake a stall.
      // Visibility-return handles reconnection instead (see handleVisibilityChange).
      if (this.isHidden) return;
      if (Date.now() - this.lastActivityAt > timeoutMs) {
        console.warn(
          `${this.config.connectionName} SSE watchdog: no activity for ${timeoutMs}ms — treating as stall`
        );
        diagnosticEventLog.record(this.diagnosticSource, "warn", "activity-timeout", {
          activityTimeoutMs: timeoutMs,
        });
        this.handleFailure();
        this.cleanup({ preservePollingState: true });
        if (this.config.retryEnabled !== false) {
          this.reconnectWithDelay();
        }
      }
    }, Math.max(timeoutMs / 2, 5000));
  }

  private handleFailure() {
    this.failureCount += 1;
    const threshold = this.config.fallbackThreshold ?? 5;
    if (
      this.config.pollingConfig &&
      !this.pollingMode &&
      this.failureCount >= threshold
    ) {
      this.enterPollingMode();
    }
  }

  private scheduleStabilityReset() {
    clearTimeout(this.stabilityTimerRef);
    this.stabilityTimerRef = setTimeout(() => {
      if (this.status === "active") {
        this.failureCount = 0;
      }
    }, STABILITY_WINDOW_MS);
  }

  private enterPollingMode() {
    if (!this.config.pollingConfig || this.pollingMode) return;
    console.warn(
      `${this.config.connectionName} entering polling fallback after ${this.failureCount} failures`
    );
    diagnosticEventLog.record(this.diagnosticSource, "warn", "entering-polling-mode", {
      failureCount: this.failureCount,
    });
    this.pollingMode = true;
    this.notifyPollingModeListeners();

    const { pollingFn, intervalMs } = this.config.pollingConfig;
    const tick = async () => {
      try {
        await pollingFn();
      } catch (err) {
        console.error(
          `${this.config.connectionName} polling fallback error`,
          err
        );
      }
    };
    // Fire one immediately so the user sees fresh data as soon as fallback kicks in.
    void tick();
    this.pollingTimerRef = setInterval(tick, intervalMs);
  }

  private exitPollingMode() {
    if (!this.pollingMode) return;
    console.log(`${this.config.connectionName} exiting polling fallback`);
    diagnosticEventLog.record(this.diagnosticSource, "info", "sse-recovery-success", {
      reason: "sse-reconnected",
    });
    clearInterval(this.pollingTimerRef);
    this.pollingTimerRef = undefined;
    this.pollingMode = false;
    this.failureCount = 0;
    this.recoveryInFlight = false;
    this.notifyPollingModeListeners();
  }

  private notifyPollingModeListeners() {
    this.pollingModeListeners.forEach((cb) => {
      try {
        cb(this.pollingMode);
      } catch (err) {
        console.error("Error in polling-mode listener", err);
      }
    });
  }

  // ---------------- Visibility-based recovery ----------------

  /**
   * Register the visibilitychange listener once for the lifetime of this
   * manager (module singletons — they live until page unload, so the listener
   * is intentionally never removed; cleanup() must not touch it).
   */
  private startVisibilityTracking() {
    if (this.boundVisibilityHandler || typeof document === "undefined") {
      return;
    }
    if (document.visibilityState === "hidden") {
      this.isHidden = true;
      this.hiddenSince = Date.now();
    }
    this.boundVisibilityHandler = () => this.handleVisibilityChange();
    document.addEventListener(
      "visibilitychange",
      this.boundVisibilityHandler
    );
  }

  /**
   * On a long hide the EventSource may have silently died (carrier proxy
   * idle-kill, NAT timeout) without firing onerror, and the watchdog timer was
   * frozen while hidden — so force a reconnect on return rather than trust it.
   * Short hides just reset the activity clock.
   */
  private handleVisibilityChange() {
    if (typeof document === "undefined") return;
    if (document.visibilityState === "hidden") {
      this.isHidden = true;
      this.hiddenSince = Date.now();
      return;
    }
    const hiddenDuration =
      this.hiddenSince > 0 ? Date.now() - this.hiddenSince : 0;
    this.isHidden = false;
    this.hiddenSince = 0;
    const threshold = this.config.activityTimeoutMs ?? 45000;
    if (hiddenDuration > threshold) {
      this.forceReconnectFromVisibility(hiddenDuration);
    } else {
      this.recordActivity();
    }
  }

  /**
   * Tear down the (possibly dead) connection and reconnect immediately.
   * Polling state is preserved so there is no data gap — onopen() already
   * calls exitPollingMode() once SSE is confirmed back.
   */
  private forceReconnectFromVisibility(hiddenDurationMs: number) {
    if (this.recoveryInFlight || !this.currentConnection) return;
    this.recoveryInFlight = true;

    diagnosticEventLog.record(this.diagnosticSource, "info", "force-reconnect-long-hide", {
      hiddenDurationMs,
      isPollingMode: this.pollingMode,
    });
    console.log(
      `${this.config.connectionName} tab visible after ${Math.round(
        hiddenDurationMs / 1000
      )}s — forcing reconnect`
    );

    // Status may still be a stale "active"; reset so start() doesn't early-out.
    // preservePollingState keeps the polling fallback (and failureCount)
    // running until onopen confirms SSE — users never lose data.
    this.setStatus("inactive");
    this.cleanup({ preservePollingState: true });
    this.start(this.currentConnection);
  }
}
